import { useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Checkbox,
  Button,
  Chip,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingComponent from "../components/LoadingComponent";

import { useDeliveryUpsell } from "../hooks/useDeliveryUpsell";

const DeliveryUpsell = () => {
  const navigate = useNavigate();
  const {
    deliveryUpsells,
    deliveryUpsellsCount,
    loading,
    error,
    pagination,
    manageRequests,
    handleChangePage,
    handleChangeRowsPerPage,
    fetchDeliveryUpsells,
  } = useDeliveryUpsell();

  useEffect(() => {
    fetchDeliveryUpsells();
  }, [fetchDeliveryUpsells])

  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = deliveryUpsells.map((n) => n.order_id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (_, id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  const handleAction = async (action) => {
    const actionText = action === "ACCEPT" ? "ACEPTAR" : "RECHAZAR";

    if (
      !window.confirm(
        `¿Estás seguro de ${actionText} ${selectedIds.length} envíos?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await manageRequests(selectedIds, action);

      setSelectedIds([]);
      alert(`Envíos procesados correctamente.`);
    } catch (err) {
      console.error(err);
      alert("Error al procesar la solicitud");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Tooltip title="Volver a Mis Deliveries">
          <IconButton onClick={() => navigate("/home/deliveries-resume")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography
          variant="h4"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <MoveToInboxIcon fontSize="large" color="primary" />
          Solicitudes de Envíos Entrantes
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedIds.length > 0 && (
        <Grid
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#f5f9ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // border: "1px solid #cce5ff",
          }}
        >
          <Typography variant="subtitle1" color="primary" fontWeight="bold">
            {selectedIds.length} seleccionados
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleAction("REJECT")}
              disabled={actionLoading}
            >
              Rechazar
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleAction("ACCEPT")}
              disabled={actionLoading}
            >
              Aceptar e Importar
            </Button>
          </Stack>
        </Grid>
      )}

      {actionLoading && <LinearProgress sx={{ mb: 1 }} />}

      <Grid>
        {loading && <LoadingComponent />}

        {!loading && deliveryUpsells.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="info" sx={{ display: "inline-flex" }}>
              No tienes solicitudes de envíos pendientes.
            </Alert>
          </Box>
        )}

        {!loading && deliveryUpsells.length > 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{ "& > *": { borderBottom: "unset" } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selectedIds.length > 0 &&
                          selectedIds.length < deliveryUpsells.length
                        }
                        checked={
                          deliveryUpsells.length > 0 &&
                          selectedIds.length === deliveryUpsells.length
                        }
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell>Referencia / ID</TableCell>
                    <TableCell>Partner (Origen)</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Tarifa Acordada</TableCell>
                    <TableCell>Fecha Creación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveryUpsells.map((row) => {
                    const isRowSelected = isSelected(row.order_id);
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isRowSelected}
                        tabIndex={-1}
                        key={row.upsell_id}
                        selected={isRowSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isRowSelected}
                            onClick={(event) =>
                              handleClick(event, row.order_id)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {row.reference_id}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {row.order_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.source_admin}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.commune}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {row.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`$${row.agreed_cost}`}
                            size="small"
                            variant="outlined"
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(row.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={deliveryUpsellsCount}
              rowsPerPage={pagination.rowsPerPage}
              page={pagination.page - 1}
              onPageChange={(e, newPage) => handleChangePage(newPage + 1)}
              onRowsPerPageChange={(e) =>
                handleChangeRowsPerPage(parseInt(e.target.value, 10))
              }
            />
          </>
        )}
      </Grid>
    </Box>
  );
};

export default memo(DeliveryUpsell);
